import {
  ActionPropertyDescriptionFragment,
  ActionPropertyValueFragment,
  DisplayRulesFragment,
} from '$/GraphQL/schema/types';
import React from 'react';

import type { InteractionInfo } from '../InteractionInfo';
import { ActionPropertyValue } from './ActionPropertyValue';
import { ActionPropertiesValueHash } from './types';

const getActionPropertiesValueHash = (
  properties: (ActionPropertyDescriptionFragment | ActionPropertyValueFragment)[]
): ActionPropertiesValueHash => {
  const propsHash: ActionPropertiesValueHash = {};
  properties
    .map((p) => new ActionPropertyValue(p))
    .forEach((p) => (propsHash[p.prop.property] = p));
  return propsHash;
};

type MatchDisplayRulesCallback = (rules: DisplayRulesFragment[]) => boolean;
const useMatchDisplayRulesCallback = (
  actionProperties: ActionPropertyValueFragment[] = []
): MatchDisplayRulesCallback => {
  const actionPropertiesValueHash = React.useMemo(
    () => getActionPropertiesValueHash(actionProperties),
    [actionProperties]
  );

  const matchDisplayRules = React.useCallback(
    (rules: DisplayRulesFragment[]): boolean => {
      if (rules.length === 0) return true;
      const hasUnmatchedRule = rules
        .filter((r) => Boolean(r.property))
        .some((rule) => {
          const prodValue = actionPropertiesValueHash[rule.property!];
          if (prodValue && !prodValue.performDisplayRule({ actionPropertiesValueHash }, rule)) {
            return true;
          }
          return false;
        });
      return !hasUnmatchedRule;
    },

    [actionPropertiesValueHash]
  );

  return matchDisplayRules;
};

type InteractionMatchDisplayRulesCallback = (interaction: InteractionInfo) => boolean;
export const useInteractionMatchDisplayRulesCallback = (
  actionProperties: ActionPropertyValueFragment[]
): InteractionMatchDisplayRulesCallback => {
  const matchDisplayRules = useMatchDisplayRulesCallback(actionProperties);

  const matchDisplayRulesForInteraction = React.useCallback(
    (interaction: InteractionInfo): boolean => {
      const rules = interaction.details?.displayRules ?? [];
      return matchDisplayRules(rules);
    },
    [matchDisplayRules]
  );

  return matchDisplayRulesForInteraction;
};
